import { motion } from "framer-motion";
import { Check, Flame, ArrowUpRight, BadgeIndianRupee } from "lucide-react";
import { useLang } from "@/i18n";

const Packages = ({ packages, onEnroll }) => {
  const { lang, t } = useLang();

  return (
    <section id="packages" className="relative py-24 sm:py-32" data-testid="packages-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-10 max-w-2xl"
        >
          <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt" data-testid="packages-kicker">
            — {t.packages_kicker}
          </p>
          <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="packages-title">
            {t.packages_title}
          </h2>
          <p className="mt-4 text-base text-zinc-400 sm:text-lg" data-testid="packages-subtitle">{t.packages_sub}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 flex items-center gap-3.5 rounded-xl border border-volt/30 bg-volt/10 px-5 py-4"
          data-testid="admission-fee-note"
        >
          <BadgeIndianRupee className="h-6 w-6 shrink-0 text-volt" />
          <p className="text-sm font-semibold text-volt sm:text-base">{t.admission_fee_note}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="packages-grid">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              data-testid={`package-card-${pkg.slug}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`card-scan group relative flex flex-col rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1.5 ${
                pkg.popular
                  ? "border-volt bg-panel volt-glow lg:scale-[1.03]"
                  : "border-zinc-800 bg-surface hover:border-zinc-600"
              }`}
            >
              {pkg.popular && (
                <span
                  data-testid={`package-popular-badge-${pkg.slug}`}
                  className="absolute -top-3.5 left-6 flex items-center gap-1.5 rounded-full bg-volt px-3.5 py-1 font-display text-xs font-bold uppercase tracking-widest text-ink"
                >
                  <Flame className="h-3.5 w-3.5" />
                  {t.popular}
                </span>
              )}
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">{lang === "en" ? pkg.duration_en : pkg.duration_bn}</p>
              <h3 className="mt-1 font-display text-2xl font-bold uppercase tracking-wide text-white" data-testid={`package-name-${pkg.slug}`}>
                {lang === "en" ? pkg.name_en : pkg.name_bn}
              </h3>
              <div className="mt-4 flex items-end gap-1.5">
                <span className={`font-display text-5xl font-extrabold leading-none ${pkg.popular ? "text-volt" : "text-white"}`} data-testid={`package-price-${pkg.slug}`}>
                  ₹{pkg.price.toLocaleString("en-IN")}
                </span>
              </div>
              <ul className="mt-6 flex-1 space-y-3 border-t border-zinc-800 pt-6">
                {(lang === "en" ? pkg.features_en : pkg.features_bn).map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2.5 text-sm text-zinc-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`package-enroll-button-${pkg.slug}`}
                onClick={() => onEnroll(pkg.slug)}
                className={`mt-7 flex items-center justify-center gap-2 rounded-full py-3.5 font-display text-sm font-bold uppercase tracking-widest transition-all duration-200 hover:scale-[1.02] ${
                  pkg.popular
                    ? "bg-volt text-ink hover:bg-volt-hover"
                    : "border border-zinc-700 text-white hover:border-volt hover:text-volt"
                }`}
              >
                {t.enroll_now}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Packages;
